/**
 * CRM contact-mail API client (spec-48). Mirrors the shape of
 * `features/preinvest/mailtemplates/api.ts` so the compose dialog can lean on
 * the same error handling (server `{"error": "…"}` strings surface as thrown
 * `Error.message`).
 */

import { supabase } from '@/lib/supabase'
import type { MailMessage } from '@/features/preinvest/mail/types'

export type MailDirection = 'inbound' | 'outbound'

export interface ContactMailListItem {
  id: string
  direction: MailDirection
  occurred_at: string
  subject: string | null
  counterparty: string | null
  snippet: string | null
}

export interface ContactMailListOut {
  items: ContactMailListItem[]
  total: number
}

export interface ContactMailSendInput {
  to?: string[]
  cc?: string[]
  subject: string
  body_html: string
}

export interface ContactMailSendFromTemplateInput {
  template_id: string
  to?: string[]
  cc?: string[]
  variables?: Record<string, string>
}

async function authHeaders(): Promise<Record<string, string>> {
  const {
    data: { session },
  } = await supabase.auth.getSession()
  const headers: Record<string, string> = {
    Accept: 'application/json',
    'Content-Type': 'application/json',
  }
  if (session?.access_token) {
    headers.Authorization = `Bearer ${session.access_token}`
  }
  return headers
}

async function fail(response: Response): Promise<never> {
  let message: string | null = null
  try {
    const body = (await response.json()) as { error?: unknown }
    if (typeof body.error === 'string') message = body.error
  } catch {
    // non-JSON body — fall through to the generic message
  }
  throw new Error(message ?? `Verzoek mislukt (${response.status})`)
}

export async function sendContactMail(
  personId: string,
  input: ContactMailSendInput,
): Promise<MailMessage> {
  const response = await fetch(
    `/api/v1/crm/contacts/${personId}/mail/send`,
    {
      method: 'POST',
      headers: await authHeaders(),
      body: JSON.stringify(input),
    },
  )
  if (!response.ok) return fail(response)
  return (await response.json()) as MailMessage
}

export async function sendContactMailFromTemplate(
  personId: string,
  input: ContactMailSendFromTemplateInput,
): Promise<MailMessage> {
  const response = await fetch(
    `/api/v1/crm/contacts/${personId}/mail/send-from-template`,
    {
      method: 'POST',
      headers: await authHeaders(),
      body: JSON.stringify(input),
    },
  )
  if (!response.ok) return fail(response)
  return (await response.json()) as MailMessage
}

export async function listContactMail(
  personId: string,
  params: { limit?: number; offset?: number } = {},
): Promise<ContactMailListOut> {
  const query = new URLSearchParams()
  if (params.limit !== undefined) query.set('limit', String(params.limit))
  if (params.offset !== undefined) query.set('offset', String(params.offset))
  const qs = query.toString()
  const response = await fetch(
    `/api/v1/crm/contacts/${personId}/mail${qs ? `?${qs}` : ''}`,
    { headers: await authHeaders() },
  )
  if (!response.ok) return fail(response)
  return (await response.json()) as ContactMailListOut
}

export async function getContactMailMessage(
  personId: string,
  messageId: string,
): Promise<MailMessage> {
  const response = await fetch(
    `/api/v1/crm/contacts/${personId}/mail/${messageId}`,
    { headers: await authHeaders() },
  )
  if (!response.ok) return fail(response)
  return (await response.json()) as MailMessage
}
